import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentsApi } from '../api/payments';
import { QUERY_KEYS } from '../utils/constants';

export type { MembershipPlan, UserMembership, PaymentRecord, CardToken, ActiveMembershipResponse, SubUser, BatchVietQRCreateResponse, OrderItem, FamilyMembership, PendingMemberRequest, MembershipChangePlanResponse, MemberPlanCatalogEntry, MemberPlanOffer, MemberPlanCatalogResponse } from '../api/payments';

const CARDS_KEY = 'savedCards';
const ACTIVE_MEMBERSHIP_KEY = 'activeMembership';

export function useMembershipPlans() {
  return useQuery({
    queryKey: [QUERY_KEYS.plans],
    queryFn: paymentsApi.getPlans,
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * Fetch the context-aware plan catalog for the current user + optional family members.
 * `memberUserIds` should be [payerUserId, ...dependentUserIds] in order.
 * Pass null/undefined to fetch only for the current user.
 */
export function usePlanCatalog(memberUserIds?: string[] | null) {
  return useQuery({
    queryKey: [QUERY_KEYS.plans, 'catalog', memberUserIds ?? []],
    queryFn: () => paymentsApi.getPlanCatalog(memberUserIds ?? undefined),
    // staleTime: 0 — the catalog is keyed by exact member composition (including
    // dummy UUIDs for pending members). Whenever a member is added or removed the
    // key changes, so React Query would normally use the cached value within the
    // stale window. We want a fresh fetch every time the composition changes so
    // tier-discount indices are always recalculated from the current member list.
    staleTime: 0,
  });
}

export function useActiveMembership() {
  return useQuery({
    queryKey: [ACTIVE_MEMBERSHIP_KEY],
    queryFn: paymentsApi.getActiveMembership,
    staleTime: 60 * 1000,
  });
}

export function useSavedCards() {
  return useQuery({
    queryKey: [CARDS_KEY],
    queryFn: paymentsApi.getSavedCards,
  });
}

export function useInitiatePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.initiatePayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.paymentHistory] });
    },
  });
}

export function useTokenPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.tokenPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.paymentHistory] });
    },
  });
}

export function useDeleteSavedCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.deleteSavedCard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CARDS_KEY] });
    },
  });
}

export function useCreateSubUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.createSubUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subUsers'] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.dependents] });
    },
  });
}

export function useInitiateOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.initiateOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.paymentHistory] });
      queryClient.invalidateQueries({ queryKey: [ACTIVE_MEMBERSHIP_KEY] });
      queryClient.invalidateQueries({ queryKey: ['subUsers'] });
      queryClient.invalidateQueries({ queryKey: ['familyMemberships'] });
    },
  });
}

export function useUpdateOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.updateOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.paymentHistory] });
      queryClient.invalidateQueries({ queryKey: [ACTIVE_MEMBERSHIP_KEY] });
      queryClient.invalidateQueries({ queryKey: ['subUsers'] });
      queryClient.invalidateQueries({ queryKey: ['familyMemberships'] });
    },
  });
}

export function useFamilyMemberships() {
  return useQuery({
    queryKey: ['familyMemberships'],
    queryFn: paymentsApi.getFamilyMemberships,
  });
}

export function useFamilyPaymentHistory() {
  return useQuery({
    queryKey: ['familyPaymentHistory'],
    queryFn: paymentsApi.getFamilyPaymentHistory,
  });
}

export function useChangeMembershipPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, newPlanId, language }: { membershipId: string; newPlanId: string; language?: string }) =>
      paymentsApi.changeMembershipPlan(membershipId, newPlanId, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['familyMemberships'] });
      queryClient.invalidateQueries({ queryKey: [ACTIVE_MEMBERSHIP_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.memberships] });
    },
  });
}

export function useBatchChangeMembershipPlans() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.batchChangeMembershipPlans,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['familyMemberships'] });
      queryClient.invalidateQueries({ queryKey: [ACTIVE_MEMBERSHIP_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.memberships] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.paymentHistory] });
    },
  });
}

export function useScheduleMembershipCancel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) => paymentsApi.scheduleMembershipCancel(membershipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['familyMemberships'] });
      queryClient.invalidateQueries({ queryKey: [ACTIVE_MEMBERSHIP_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.memberships] });
    },
  });
}

export function useClearScheduledChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) => paymentsApi.clearScheduledChange(membershipId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['familyMemberships'] });
      queryClient.invalidateQueries({ queryKey: [ACTIVE_MEMBERSHIP_KEY] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.memberships] });
      queryClient.refetchQueries({ queryKey: ['familyMemberships'] });
    },
  });
}

export function useMyMemberships() {
  return useQuery({
    queryKey: [QUERY_KEYS.memberships],
    queryFn: paymentsApi.getMyMemberships,
  });
}

export function useMarkVietQRPaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.markVietQRPaid,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIVE_MEMBERSHIP_KEY] });
    },
  });
}

export function usePendingVerification() {
  return useQuery({
    queryKey: ['pendingVerification'],
    queryFn: paymentsApi.getPendingVerification,
  });
}

// ---------------------------------------------------------------------------
// Pause hooks
// ---------------------------------------------------------------------------

export function usePauseHistory(membershipId: string | null) {
  return useQuery({
    queryKey: ['pauseHistory', membershipId],
    queryFn: () => paymentsApi.getPauseHistory(membershipId!),
    enabled: !!membershipId,
  });
}

export function useResumeMembership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, resumeReason }: { membershipId: string; resumeReason?: string }) =>
      paymentsApi.resumeMembership(membershipId, resumeReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['familyMemberships'] });
      queryClient.invalidateQueries({ queryKey: [ACTIVE_MEMBERSHIP_KEY] });
      queryClient.invalidateQueries({ queryKey: ['pauseHistory'] });
    },
  });
}
